import './index.less';
import { isNumber, pull, uniq } from 'lodash';
import React, { useCallback, useEffect, useRef } from 'react';
import {
    BaseViewportPlugin,
    getFramework,
    getLayerId,
    getMouseMoveRect,
    getOperateType,
    getPointRect,
    MoveListenerLevel,
    PointMove,
    useIdentity,
    useIsLongImageMode,
    useMoveEventListener,
} from '@/framework';
import { getLocaleStore } from '@/i18n';
import { type GroupLayer } from '@/layers/group-layer';
import { type Layer } from '@/layers/layer';
import { getLayerAgentOperateConfig } from '@/utils/agent-teams-kit';
import { getCollisionIds, getRotateRect, IShape } from '@/utils/collision-kit';
import { isHotKeyPress, isLeftButton } from '@/utils/event-kit';
import { BaseLayerViewType, IExportPlugin, IPoint, PluginType } from '@/version-types';
import { getMoveState } from '../active/states/move-state';
import { CLEAR_MOUSE_OPERATE, DRAW_MOUSE_OPERATE } from '../event-name';
import { resolveFormatBrushTargetId } from '../format-brush-target';
import { type MouseOperate } from '../mouse-operate';
import { getGroupInnerState } from './states/group-inner-state';

function tryGetGroupChildIds(identity: string, brokeExtraGroup = true, autoRerender = false) {
    const framework = getFramework(identity);
    const activeLayer = framework.service.frame.getActiveLayer();
    if (!activeLayer) return;
    const ids: string[] = [];
    if (framework.service.flatLayer.checkIsExtraGroup(activeLayer)) {
        (activeLayer.children || []).forEach((item) => {
            ids.push(item.id);
        });
        if (brokeExtraGroup) {
            const res = framework.service.frame.tryRemoveGroup(activeLayer);
            if (res && autoRerender) {
                framework.service.frame.delActiveLayer();
            }
        }
    } else {
        ids.push(activeLayer.id);
    }
    return ids;
}

const PickInteraction = React.memo(() => {
    const identity = useIdentity();
    const framework = getFramework(identity);
    const isLongImageMode = useIsLongImageMode();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const moveRectRef = useRef<IShape | null>(null);
    const collisionsRef = useRef<IShape[]>([]);
    const groupPickIdsRef = useRef<string[]>([]);
    const hotKeyPickFrameIdRef = useRef<number | null>(null);
    const renderRectRef = useRef(false);
    const pickGroupInnerIdRef = useRef('');
    const formatBrushPointerRef = useRef(false);
    const groupInnerState = getGroupInnerState(identity);
    const innerIds = useRef<string[]>([]);
    const startPointRef = useRef<{ ui: IPoint; scene: IPoint }>({
        ui: { x: 0, y: 0 },
        scene: { x: 0, y: 0 },
    });
    const shapesRef = useRef<{ [key: number]: IShape[] }>({});
    const defaultShapes = useRef<IShape[]>([]);
    /** 框选绘制颜色 */
    const strokeStyleColorRef = useRef<string>('');

    const tryGetGroupLayer = useCallback(() => {
        const groupLayer = framework.service.registration.getInstance<GroupLayer>('GroupLayer');
        if (!groupLayer) {
            throw new Error('GroupLayer is not found');
        }
        return groupLayer;
    }, []);

    const tryGetLayer = useCallback(() => {
        const layer = framework.service.registration.getInstance<Layer>('Layer');
        if (!layer) {
            throw new Error('Layer is not found');
        }
        return layer;
    }, []);

    /** 获取与框选矩形相交的 frame ids */
    const getIntersectFrameIds = useCallback((moveCollisionRect: IShape) => {
        if (!framework.service.edgelessCanvas.isEdgelessMode) return [];

        const frameShapes = framework.service.frame
            .getRenderFrameIds()
            .filter((frameId) => framework.service.frame.get(frameId))
            .map((frameId) => framework.service.frame.getShape(frameId));

        return getCollisionIds(moveCollisionRect, frameShapes)
            .map(Number)
            .filter((frameId) => Number.isFinite(frameId));
    }, []);

    /** 获取被框选矩形完整覆盖的 frame ids */
    const getContainedFrameIds = useCallback((moveCollisionRect: IShape) => {
        if (!framework.service.edgelessCanvas.isEdgelessMode) return [];

        const rectLeft = moveCollisionRect.left;
        const rectTop = moveCollisionRect.top;
        const rectRight = moveCollisionRect.left + moveCollisionRect.width;
        const rectBottom = moveCollisionRect.top + moveCollisionRect.height;

        return framework.service.frame
            .getRenderFrameIds()
            .filter((frameId) => {
                if (!framework.service.frame.get(frameId)) return false;
                const shape = framework.service.frame.getShape(frameId);
                const { x1, y1, x2, y2 } = getRotateRect({
                    left: shape.left,
                    top: shape.top,
                    width: shape.width,
                    height: shape.height,
                    rotate: shape.rotate ?? 0,
                });
                return x1 >= rectLeft && y1 >= rectTop && x2 <= rectRight && y2 <= rectBottom;
            })
            .map(Number)
            .filter((frameId) => Number.isFinite(frameId));
    }, []);

    // 多选快捷键
    useEffect(() => {
        const { $t } = getLocaleStore();
        const mouseLeftKey = $t((v) => v.Text_MouseLeft);

        framework.service.hotkey.register({
            name: $t((v) => v.Text_SelectMultiple),
            win: 'Ctrl ' + mouseLeftKey,
            mac: '⌘ ' + mouseLeftKey,
            key: 'select',
        });
    }, []);

    // 实现鼠标点击
    useEffect(() => {
        const moveKit = new PointMove();

        const downHandle = framework.service.editor.onMouseDown((e) => {
            if (!isLeftButton(e)) return;
            moveKit.setStart({ x: e.pageX, y: e.pageY });
            const formatBrushState = framework.service.formatBrush.getState();
            formatBrushPointerRef.current = !!formatBrushState;
            if (formatBrushState) return;

            const hasHotKey = isHotKeyPress();
            const activeLayer = framework.service.frame.getActiveLayer();
            let stopPropagation = hasHotKey;

            if (!hasHotKey && activeLayer && !framework.service.frame.getHoverId()) {
                const mouseDownRect = getPointRect(framework.service.transform.getScenePoint(e));
                const layerId = framework.service.transform.tryPickWrap(
                    mouseDownRect,
                    framework.service.frame.getCollisionLayers(
                        activeLayer.__originIndex ?? framework.service.frame.getActiveFrameId(),
                        true,
                        true,
                    ),
                );

                if (layerId && layerId !== activeLayer.id) {
                    const layer = framework.service.flatLayer.getLayer(layerId);
                    if (framework.service.flatLayer.checkIsGroup(layer)) {
                        const activeInfo = framework.service.frame.getActiveInfo();
                        const pickChildId = framework.service.transform.tryPickChild(mouseDownRect, layer.children);
                        if (pickChildId && pickChildId !== activeInfo?.info.pickId) {
                            pickGroupInnerIdRef.current = layerId;
                            stopPropagation = true;
                        }
                    } else {
                        pickGroupInnerIdRef.current = layerId;
                        stopPropagation = true;
                    }
                }

                if (!stopPropagation && framework.service.flatLayer.checkIsFormalGroup(activeLayer)) {
                    const activeInfo = framework.service.frame.getActiveInfo();
                    const pickOtherChildId = framework.service.transform.tryPickChild(
                        mouseDownRect,
                        activeLayer.children,
                    );
                    if (pickOtherChildId && pickOtherChildId !== activeInfo?.info.pickId) {
                        pickGroupInnerIdRef.current = pickOtherChildId;
                        stopPropagation = true;
                    }
                    if (!stopPropagation) {
                        let isPickCurrent = false;
                        if (activeInfo?.info.pickId) {
                            isPickCurrent = !!framework.service.transform.tryPickChild(mouseDownRect, [
                                framework.service.flatLayer.getLayer(activeInfo.info.pickId),
                            ]);
                        }
                        const operateType = getOperateType(e.target);
                        const isMoveOperate = operateType === BaseLayerViewType.MOVE || !operateType;

                        if (!isPickCurrent && isMoveOperate) {
                            const groupInnerId = framework.service.transform.tryPickChild(
                                mouseDownRect,
                                activeLayer.children,
                            );
                            if (groupInnerId) {
                                stopPropagation = true;
                                pickGroupInnerIdRef.current = groupInnerId;
                            } else {
                                const originIndex = framework.service.frame.getLayerFrameId(activeLayer.id);
                                if (isNumber(originIndex)) {
                                    const groupOuterId = framework.service.transform.tryPickWithoutId(
                                        mouseDownRect,
                                        framework.service.frame.getCollisionLayers(originIndex, true, true),
                                        activeLayer.id,
                                    );
                                    if (groupOuterId) {
                                        stopPropagation = true;
                                        framework.service.frame.setActiveLayer(groupOuterId);

                                        // console.log('groupOuterId =>', groupOuterId);
                                    }
                                }
                            }
                        }
                        if (isPickCurrent && activeLayer.lock) {
                            stopPropagation = true;
                        }
                    }
                }
            }

            return stopPropagation;
        }, 1);

        const upHandle = framework.service.editor.onMouseUp((e) => {
            if (!isLeftButton(e)) return;
            if (formatBrushPointerRef.current) {
                formatBrushPointerRef.current = false;
                return true;
            }
            const hasHotKey = isHotKeyPress();
            moveKit.setEnd({ x: e.pageX, y: e.pageY });
            const isMove = moveKit.isMove();
            let stopPropagation = hasHotKey || isMove;
            const activeLayer = framework.service.frame.getActiveLayer();
            const downGroupInnerId = pickGroupInnerIdRef.current;
            pickGroupInnerIdRef.current = '';

            if (!isMove) {
                if (downGroupInnerId && !tryGetLayer().checkWrapLayerProps(downGroupInnerId, 'hidden', true)) {
                    framework.service.frame.setActiveLayer(downGroupInnerId);
                } else {
                    const mouseDownRect = getPointRect(framework.service.transform.getScenePoint(e));
                    if (hasHotKey) {
                        let targetLayerId = framework.service.frame.getHoverId();
                        if (!targetLayerId || targetLayerId !== activeLayer?.id) {
                            if (!targetLayerId) {
                                if (activeLayer) {
                                    if (framework.service.flatLayer.checkIsExtraGroup(activeLayer)) {
                                        const groupInnerId = framework.service.transform.tryPickWrap(
                                            mouseDownRect,
                                            activeLayer.children,
                                        );
                                        // 临时组内，取消选中
                                        if (groupInnerId) {
                                            const childIds = tryGetGroupChildIds(identity, true, true);
                                            if (childIds) {
                                                pull(childIds, groupInnerId);
                                                if (childIds.length) {
                                                    stopPropagation = true;
                                                    if (childIds.length === 1) {
                                                        framework.service.frame.setActiveLayer(childIds[0]);
                                                    } else {
                                                        tryGetGroupLayer().createGroupAndActive(childIds);
                                                    }
                                                }
                                            }
                                        } else {
                                            // 临时组外，选中
                                            targetLayerId = framework.service.transform.tryPickWithoutId(
                                                mouseDownRect,
                                                framework.service.frame.getCollisionLayers(
                                                    activeLayer.__originIndex ??
                                                        framework.service.frame.getActiveFrameId(),
                                                    true,
                                                    true,
                                                ),
                                                activeLayer.id,
                                            );
                                        }
                                    } else if (framework.service.flatLayer.checkIsFormalGroup(activeLayer)) {
                                        const groupInnerId = framework.service.transform.tryPickChild(
                                            mouseDownRect,
                                            activeLayer.children,
                                        );
                                        if (!groupInnerId) {
                                            const originIndex = framework.service.frame.getLayerFrameId(activeLayer.id);
                                            if (isNumber(originIndex)) {
                                                targetLayerId = framework.service.transform.tryPickWithoutId(
                                                    mouseDownRect,
                                                    framework.service.frame.getCollisionLayers(originIndex, true, true),
                                                    activeLayer.id,
                                                );
                                            }
                                        }
                                    } else {
                                        targetLayerId = framework.service.transform.tryPickWithoutId(
                                            mouseDownRect,
                                            framework.service.frame.getCollisionLayers(
                                                activeLayer.__originIndex ?? framework.service.frame.getActiveFrameId(),
                                            ),
                                            activeLayer.id,
                                        );
                                    }
                                }
                            }
                        } else {
                            if (activeLayer) {
                                const originIndex = framework.service.frame.getLayerFrameId(activeLayer.id);
                                if (isNumber(originIndex)) {
                                    targetLayerId = framework.service.transform.tryPickWithoutId(
                                        mouseDownRect,
                                        framework.service.frame.getCollisionLayers(originIndex, true, true),
                                        activeLayer.id,
                                    );
                                }
                            }
                        }
                        if (targetLayerId) {
                            const childIds = tryGetGroupChildIds(identity, true, false);
                            stopPropagation = true;
                            if (childIds) {
                                childIds.push(targetLayerId);
                                // 如果存在锁定图层，则选中
                                let hasLock = false;
                                for (let i = 0; i < childIds.length; i++) {
                                    const id = childIds[i];
                                    const layer = framework.service.flatLayer.getLayer(id);
                                    if (layer.lock) {
                                        hasLock = true;
                                        break;
                                    }
                                }
                                if (hasLock) {
                                    framework.service.frame.setActiveLayer(targetLayerId);
                                } else {
                                    tryGetGroupLayer().createGroupAndActive(childIds);
                                }
                            } else {
                                framework.service.frame.setActiveLayer(targetLayerId);
                            }
                        }
                    } else {
                        if (activeLayer && framework.service.flatLayer.checkIsExtraGroup(activeLayer)) {
                            const mouseDownRect = getPointRect(framework.service.transform.getScenePoint(e));
                            const groupInnerId = framework.service.transform.tryPickWrap(
                                mouseDownRect,
                                activeLayer.children,
                            );
                            if (groupInnerId) {
                                stopPropagation = true;
                                framework.service.frame.setActiveLayer(groupInnerId);
                            } else {
                                const originIndex = framework.service.frame.getLayerFrameId(activeLayer.id);
                                if (isNumber(originIndex)) {
                                    const groupOuterId = framework.service.transform.tryPickWithoutId(
                                        mouseDownRect,
                                        framework.service.frame.getCollisionLayers(originIndex, true, true),
                                        activeLayer.id,
                                    );
                                    if (groupOuterId) {
                                        stopPropagation = true;
                                        framework.service.frame.setActiveLayer(groupOuterId);
                                    }
                                }
                            }
                        }
                    }
                }
            }
            return stopPropagation;
        }, 1);
        return () => {
            framework.service.events.off(downHandle);
            framework.service.events.off(upHandle);
        };
    }, []);

    const disposePick = useCallback(() => {
        collisionsRef.current = [];
        shapesRef.current = {};
        groupPickIdsRef.current = [];
        hotKeyPickFrameIdRef.current = null;
        defaultShapes.current = [];
        framework.service.frame.clearSelected();
        moveRectRef.current = null;
        framework.service.events.emit(DRAW_MOUSE_OPERATE);
        framework.service.editor.setIsMouseSelect(false);
    }, []);

    useEffect(() => {
        const spaceHandle = framework.service.hotkey.onSpace((args) => {
            if (args.isKeydown) {
                disposePick();
            }
        });
        return () => {
            framework.service.events.off(spaceHandle);
        };
    }, []);

    const canSelect = useRef(true);
    // 实现鼠标框选
    useMoveEventListener(
        (e) => {
            if (framework.service.edgelessCanvas.isEdgelessMode) {
                if (e.isEnd) {
                    if (!canSelect.current) {
                        canSelect.current = true;
                        return;
                    }
                }
                if (e.isStart) {
                    const selectedFrameIds = framework.service.frame.getFrameSelectionFrameIds();
                    if (selectedFrameIds.length) {
                        canSelect.current = false;
                    }
                    // console.log('move 1 => ', { canSelect: canSelect.current });
                }
            }

            pickGroupInnerIdRef.current = '';
            renderRectRef.current = false;
            const isDisableOperate = framework.service.editor.disableOperate;
            const isDisablePick = framework.service.editor.disablePick;
            const isDisableMouseSelect = framework.service.editor.disableMouseSelect;
            if (isDisableOperate || isDisablePick || isDisableMouseSelect || !canSelect.current) return;

            if (formatBrushPointerRef.current || framework.service.formatBrush.getState()) return;

            const isInViewport = framework.service.transform.checkPointInViewport(e.startPoint);
            if (!isInViewport) {
                return;
            }

            const operateType = getOperateType(e.target);
            if (operateType) {
                return;
            }

            const moveLayerId = getMoveState(identity).getMoveLayerId();
            if (moveLayerId) {
                return;
            }

            // console.log('move 2 => ', { canSelect: canSelect.current });

            if (e.isStart) {
                startPointRef.current.ui = framework.service.transform.getViewportPoint(e.startPoint);
                startPointRef.current.scene = framework.service.transform.getScenePoint(e.originEvent);
            }

            const activeLayer = framework.service.frame.getActiveLayer();
            if (groupInnerState.getIsActive() && activeLayer?.children) {
                // 组内框选逻辑
                if (e.isStart) {
                    framework.service.events.emit(DRAW_MOUSE_OPERATE);
                    innerIds.current = groupInnerState.getPickIds();
                    collisionsRef.current = framework.service.transform.convertToShapes(activeLayer.children, true);
                }
                renderRectRef.current = !e.isEnd;

                // 获取鼠标选择区域，进行绘制鼠标选择框
                const moveRect = getMouseMoveRect(
                    startPointRef.current.ui,
                    framework.service.transform.getViewportPoint(e.currentPoint),
                );
                moveRectRef.current = moveRect;
                framework.service.events.emit(DRAW_MOUSE_OPERATE);
                // setMoveRect(moveRect);
                framework.service.editor.setIsMouseSelect(true);

                // 获取碰撞的图层id，进行绘制图层边框
                const moveCollisionRect = getMouseMoveRect(
                    startPointRef.current.scene,
                    framework.service.transform.getScenePoint(e.originEvent),
                );

                const collisionsIds = getCollisionIds(moveCollisionRect, collisionsRef.current);
                const ids = uniq(collisionsIds.concat(innerIds.current));

                groupInnerState.setPickIds(ids, true);

                if (e.isEnd) {
                    innerIds.current = [];
                    moveRectRef.current = null;
                    framework.service.events.emit(DRAW_MOUSE_OPERATE);
                    framework.service.editor.setIsMouseSelect(false);
                }

                return;
            }

            if (activeLayer?.lock) {
                framework.service.frame.delActiveLayer();
            } else {
                const layerId = getLayerId(identity, e.target);
                if (layerId && !framework.service.flatLayer.getLayer(layerId).lock) {
                    return;
                }
            }

            if (e.isStart) {
                framework.service.events.emit(DRAW_MOUSE_OPERATE);
            }

            const hasHotKey = isHotKeyPress();
            if (e.isStart && !hasHotKey && isLongImageMode) {
                const stageIndex = framework.service.transform.getFrameIndexByMousePointY(e.startPoint.y);
                const activeStageIndex = framework.service.frame.getActiveIndex();
                if (stageIndex !== activeStageIndex) {
                    framework.service.frame.setActiveIndex(stageIndex);
                }
            }
            if (e.isStart && hasHotKey) {
                const activeLayerFrameId = activeLayer ? framework.service.frame.getLayerFrameId(activeLayer.id) : null;
                hotKeyPickFrameIdRef.current = isNumber(activeLayerFrameId) ? activeLayerFrameId : null;
                const childIds = tryGetGroupChildIds(identity, true, true);
                if (childIds) {
                    groupPickIdsRef.current = childIds;
                    framework.service.frame.delActiveLayer();
                }
            }

            if (!hasHotKey) {
                groupPickIdsRef.current = [];
                hotKeyPickFrameIdRef.current = null;
            }

            if (e.isStart) {
                const hotKeyPickStageIndex = isNumber(hotKeyPickFrameIdRef.current)
                    ? framework.service.frame.frameIdToIndex(hotKeyPickFrameIdRef.current)
                    : null;
                const pickStageIndex =
                    isNumber(hotKeyPickStageIndex) && hotKeyPickStageIndex >= 0
                        ? hotKeyPickStageIndex
                        : framework.service.frame.getActiveIndex();

                defaultShapes.current = framework.service.transform.getShapes(pickStageIndex, false);
                collisionsRef.current = defaultShapes.current;
            }

            renderRectRef.current = !e.isEnd;
            // 获取鼠标选择区域，进行绘制鼠标选择框
            const moveRect = getMouseMoveRect(
                startPointRef.current.ui,
                framework.service.transform.getViewportPoint(e.currentPoint),
            );
            moveRectRef.current = moveRect;
            framework.service.editor.setIsMouseSelect(true);

            const currentScenePoint = framework.service.transform.getScenePoint(e.originEvent);
            // 获取碰撞的图层id，进行绘制图层边框
            const moveCollisionRect = getMouseMoveRect(startPointRef.current.scene, currentScenePoint);
            const isHotKeyPickInActiveFrame = hasHotKey && isNumber(hotKeyPickFrameIdRef.current);
            const intersectFrameIds = isHotKeyPickInActiveFrame ? [] : getIntersectFrameIds(moveCollisionRect);
            const containedFrameIds = isHotKeyPickInActiveFrame ? [] : getContainedFrameIds(moveCollisionRect);
            // 优先级：与多个 frame 相交 → 多 frame 框选；否则完整覆盖单个 frame → 单 frame 框选
            const targetFrameIds =
                intersectFrameIds.length > 1
                    ? intersectFrameIds
                    : containedFrameIds.length >= 1
                      ? containedFrameIds
                      : [];
            const boxSelectableFrameIds = framework.service.frame.filterFrameSelectionGroupFrameIds(targetFrameIds);
            if (!isHotKeyPickInActiveFrame && boxSelectableFrameIds.length) {
                framework.service.frame.clearSelected();
                framework.service.frame.delActiveLayer();
                framework.service.frame.setFrameSelectionFrameIds(boxSelectableFrameIds);
                framework.service.frame.setPickActiveFrame(true);
                framework.service.events.emit(DRAW_MOUSE_OPERATE);

                if (e.isEnd) {
                    framework.service.frame.delHoverId();
                    disposePick();
                    framework.service.frame.setFrameSelectionFrameIds(boxSelectableFrameIds);
                    framework.service.frame.setPickActiveFrame(true);
                }

                return;
            }

            framework.service.frame.clearFrameSelection();
            framework.service.frame.setHoverFrameId(null);
            framework.service.frame.setPickActiveFrame(false);
            const collisionsIds = getCollisionIds(moveCollisionRect, collisionsRef.current);
            const ids = uniq(collisionsIds.concat(groupPickIdsRef.current));
            if (!ids.length && !isHotKeyPickInActiveFrame) {
                const frameId = framework.service.frame.pick(moveCollisionRect);
                if (isNumber(frameId)) {
                    const stageIndex = framework.service.frame.frameIdToIndex(frameId);
                    if (!shapesRef.current[stageIndex]) {
                        shapesRef.current[stageIndex] = framework.service.transform.getShapes(stageIndex, false);
                    }
                    collisionsRef.current = shapesRef.current[stageIndex];
                } else {
                    collisionsRef.current = defaultShapes.current;
                }
            }
            framework.service.frame.setSelected(ids);

            if (e.isEnd && ids.length) {
                if (ids.length === 1) {
                    framework.service.frame.setActiveLayer(ids[0]);
                } else {
                    tryGetGroupLayer().createGroupAndActive(ids);
                }
            }

            framework.service.events.emit(DRAW_MOUSE_OPERATE);

            if (e.isEnd) {
                framework.service.frame.delHoverId();
                disposePick();
            }
        },
        MoveListenerLevel.DEFAULT_LEVEL,
        'pick-plugin',
    );

    useEffect(() => {
        const createRoundRectPath = (
            ctx: CanvasRenderingContext2D,
            x: number,
            y: number,
            width: number,
            height: number,
            radius: number,
        ) => {
            const finalRadius = Math.min(radius, width / 2, height / 2);

            ctx.beginPath();
            ctx.moveTo(x + finalRadius, y);
            ctx.lineTo(x + width - finalRadius, y);
            ctx.arcTo(x + width, y, x + width, y + finalRadius, finalRadius);
            ctx.lineTo(x + width, y + height - finalRadius);
            ctx.arcTo(x + width, y + height, x + width - finalRadius, y + height, finalRadius);
            ctx.lineTo(x + finalRadius, y + height);
            ctx.arcTo(x, y + height, x, y + height - finalRadius, finalRadius);
            ctx.lineTo(x, y + finalRadius);
            ctx.arcTo(x, y, x + finalRadius, y, finalRadius);
            ctx.closePath();
        };
        const setStrokeStyleColor = () => {
            strokeStyleColorRef.current = getComputedStyle(document.documentElement)
                .getPropertyValue('--stroke-cursor-target-color')
                .trim();
        };
        const drawLayerBorder = (id: string, ctx: CanvasRenderingContext2D, inner = false) => {
            const layer = framework.service.flatLayer.convertLayer(id);
            const translateX = framework.service.edgelessCanvas.translateX;
            const translateY = framework.service.edgelessCanvas.translateY;
            const scale = framework.service.edgelessCanvas.zoom;

            if (layer) {
                ctx.save();
                const height = layer.height * scale;
                const width = layer.width * scale;
                const left = layer.left * scale + translateX;
                const top = layer.top * scale + translateY;
                ctx.translate(left + width / 2, top + height / 2);
                ctx.rotate((layer.rotate * Math.PI) / 180);
                ctx.translate(-width / 2, -height / 2);
                ctx.lineWidth = 2;
                if (framework.service.flatLayer.checkIsGroup(layer) && !inner) {
                    ctx.setLineDash([5, 5]);
                }
                ctx.strokeStyle = strokeStyleColorRef.current;
                const borderRadius = (getLayerAgentOperateConfig(layer)?.style?.borderRadius || 0) * scale;

                if (borderRadius > 0) {
                    createRoundRectPath(ctx, -1, -1, width + 2, height + 2, borderRadius);
                    ctx.stroke();
                } else {
                    ctx.strokeRect(-1, -1, width + 2, height + 2);
                }
                ctx.restore();
            }
        };
        const drawMain = () => {
            if (!canvasRef.current) return;
            if (!strokeStyleColorRef.current) setStrokeStyleColor();
            const { width: viewportWidth, height: viewportHeight } = framework.service.edgelessCanvas.getSize();
            canvasRef.current.width = viewportWidth;
            canvasRef.current.height = viewportHeight;
            canvasRef.current.style.width = `${viewportWidth}px`;
            canvasRef.current.style.height = `${viewportHeight}px`;
            const ctx = clearOperate();
            if (!ctx) return;

            const activeLayer = framework.service.frame.getActiveLayer();
            if (activeLayer) {
                const isExtraGroup = framework.service.flatLayer.checkIsExtraGroup(activeLayer);
                if (isExtraGroup && !framework.service.editor.isBaseOperate) {
                    const childrenIds = activeLayer.children?.map((item) => item.id) || [];
                    for (let i = 0; i < childrenIds.length; i++) {
                        const id = childrenIds[i];
                        drawLayerBorder(id, ctx, true);
                    }
                }
            }

            const selectedInfo = framework.service.frame.getSelectedIds();
            if (selectedInfo && selectedInfo.size) {
                const arrIds = Array.from(selectedInfo);
                for (let i = arrIds.length - 1; i >= 0; i--) {
                    const id = arrIds[i];
                    drawLayerBorder(id, ctx);
                }
            }

            if (moveRectRef.current) {
                const mouseOperate = framework.service.registration.getInstance<MouseOperate>('MouseOperate');
                const moveRect = moveRectRef.current;
                ctx.save();
                ctx.lineWidth = 2;
                ctx.strokeStyle = mouseOperate.strokeColor;
                ctx.strokeRect(moveRect.left + 1, moveRect.top + 1, moveRect.width - 2, moveRect.height - 2);
                ctx.restore();
                ctx.save();
                ctx.fillStyle = mouseOperate.fillColor;
                ctx.fillRect(moveRect.left + 2, moveRect.top + 2, moveRect.width - 4, moveRect.height - 4);
                ctx.restore();
            }
        };
        const clearOperate = () => {
            if (!canvasRef.current) return;
            const ctx = canvasRef.current.getContext('2d');
            if (!ctx) return;
            const { width: viewportWidth, height: viewportHeight } = framework.service.edgelessCanvas.getSize();
            ctx.clearRect(0, 0, viewportWidth, viewportHeight);
            return ctx;
        };
        const themeChange = () => {
            setStrokeStyleColor();
            drawMain();
        };

        const themeHandle = framework.service.editor.onThemeChange(themeChange);
        const resizeHandle = framework.service.edgelessCanvas.onSizeChange(drawMain);
        const zoomHandle = framework.service.edgelessCanvas.onZoom(drawMain);
        const translateHandle = framework.service.edgelessCanvas.onPan(drawMain);
        const drawHandle = framework.service.events.on(DRAW_MOUSE_OPERATE, drawMain);
        const clearHandle = framework.service.events.on(CLEAR_MOUSE_OPERATE, clearOperate);
        const selectedIdsHandle = framework.service.frame.onSelectedIdsChange(drawMain);
        return () => {
            framework.service.events.off(themeHandle);
            framework.service.events.off(resizeHandle);
            framework.service.events.off(zoomHandle);
            framework.service.events.off(translateHandle);
            framework.service.events.off(drawHandle);
            framework.service.events.off(clearHandle);
            framework.service.events.off(selectedIdsHandle);
        };
    }, []);

    return (
        <BaseViewportPlugin name="mouse-operate-layer" zIndex={3}>
            <canvas ref={canvasRef}></canvas>
        </BaseViewportPlugin>
    );
});

const interactionPlugin: IExportPlugin = {
    type: PluginType.VIEWPORT,
    sort: 99,
    name: 'pick-layer',
    render() {
        return <PickInteraction />;
    },
};

export default interactionPlugin;
